/**
 * fitTextToBox.js — v2
 * Shared shrink-to-fit logic for the customizer text boundary box.
 * Canonical source: Rogue Wear Theme Main Branch/assets/fitTextToBox.js
 * Copied verbatim into rogue-wear-shopify-quote-api-v4/api/fitTextToBox.js and
 * rogue-wear-shopify-quote-api-v4/public/fitTextToBox.js — keep all copies in sync.
 *
 * Pure functions, no DOM/canvas API calls — caller supplies measureTextWidth.
 */

// Axis-aligned bounding box (in the box's screen-aligned frame) of a centered
// widthxheight rectangle after the same skew-then-rotate transform CSS `rotate() skew()`
// and canvas `ctx.rotate()` + `ctx.transform()` (skew matrix) apply to a point.
function computeTransformedAabb(width, height, rotationDeg, skewXDeg, skewYDeg) {
  if (!rotationDeg && !skewXDeg && !skewYDeg) {
    return { width, height };
  }
  const rot = (rotationDeg * Math.PI) / 180;
  const sx = Math.tan((skewXDeg * Math.PI) / 180);
  const sy = Math.tan((skewYDeg * Math.PI) / 180);
  const cos = Math.cos(rot);
  const sin = Math.sin(rot);
  const halfW = width / 2;
  const halfH = height / 2;
  const corners = [
    [-halfW, -halfH],
    [halfW, -halfH],
    [halfW, halfH],
    [-halfW, halfH]
  ];
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  for (let i = 0; i < corners.length; i++) {
    const x = corners[i][0];
    const y = corners[i][1];
    // Skew first, then rotate (matches CSS `rotate() skew()` and the canvas
    // backend's ctx.rotate() followed by ctx.transform() skew matrix).
    const skewedX = x + sx * y;
    const skewedY = y + sy * x;
    const rx = cos * skewedX - sin * skewedY;
    const ry = sin * skewedX + cos * skewedY;
    if (rx < minX) minX = rx;
    if (rx > maxX) maxX = rx;
    if (ry < minY) minY = ry;
    if (ry > maxY) maxY = ry;
  }
  return { width: maxX - minX, height: maxY - minY };
}

function fitTextToBox({
  text,
  startFontPx,
  minFontPx,
  boxWidthPx,
  boxHeightPx,
  rotationDeg = 0,
  skewXDeg = 0,
  skewYDeg = 0,
  lineHeightFactor = 1.2,
  step = 1,
  measureTextWidth
}) {
  const floor = Math.max(1, minFontPx);
  let fontPx = startFontPx;

  function fits(candidateFontPx) {
    const rawWidth = measureTextWidth(text, candidateFontPx);
    const rawHeight = candidateFontPx * lineHeightFactor;
    const aabb = computeTransformedAabb(rawWidth, rawHeight, rotationDeg, skewXDeg, skewYDeg);
    return aabb.width <= boxWidthPx && aabb.height <= boxHeightPx;
  }

  while (fontPx > floor) {
    if (fits(fontPx)) break;
    fontPx -= step;
  }

  fontPx = Math.max(fontPx, floor);
  const overflowed = !fits(fontPx);

  return { fontPx, overflowed };
}

// Resolves a text/logoOrText slot's `box` field to what should actually be applied at
// render time. Box is enabled by default (including for slots saved before this field
// existed, where `box` is undefined) — an admin must explicitly set `enabled: false` to
// opt a slot out. Returns null when disabled, otherwise a fully-populated box config.
const DEFAULT_BOX = { enabled: true, widthPct: 70, heightPct: 20, minSizeRem: 0.5 };
function resolveBoxConfig(box) {
  if (box && box.enabled === false) return null;
  return Object.assign({}, DEFAULT_BOX, box || {});
}

if (typeof module !== 'undefined' && module.exports) {
  module.exports = { fitTextToBox, resolveBoxConfig, DEFAULT_BOX };
} else if (typeof window !== 'undefined') {
  window.fitTextToBox = fitTextToBox;
  window.resolveBoxConfig = resolveBoxConfig;
}
